<template>
  <div class="PokemonSingleSerach" :class="[route.meta.searchMode]">
    <div class="Head">
      <Input
        placeholder="何のカードをおさがしですか？"
        search-mode
        class="SearchInput"
        v-model="singleProductSearchFormFields.keyword"
        @keyup.enter="search"
        @search="search"
      />
      <IconButton
        v-if="route.meta.searchMode === 'detail'"
        class="MenuButton"
        :showBadge="hasSingleProductSearchFormDetailFields"
        @click="
          showSingleProductSearchDetailMenu = !showSingleProductSearchDetailMenu
        "
      >
        <NarrowDown class="MenuIcon" />
      </IconButton>
    </div>

    <Transition name="accordion">
      <div
        class="DetailMenu"
        v-show="
          showSingleProductSearchDetailMenu &&
          route.meta.searchMode === 'detail'
        "
      >
        <div class="DetailForm">
          <div class="row">
            <FormBlock label="カードの種類" content-width="100%">
              <div class="kinds">
                <div class="kindsRow first">
                  <ToggleButton
                    class="ToggleButton"
                    label="ポケモン"
                    v-model="singleProductSearchFormFields.kindPokemon"
                  />
                  <ToggleButton
                    class="ToggleButton"
                    label="グッズ"
                    v-model="singleProductSearchFormFields.kindItem"
                  />
                  <ToggleButton
                    class="ToggleButton"
                    label="サポート"
                    v-model="singleProductSearchFormFields.kindSupporter"
                  />
                  <ToggleButton
                    class="ToggleButton"
                    label="スタジアム"
                    v-model="singleProductSearchFormFields.kindStadium"
                  />
                </div>
                <div class="kindsRow second">
                  <ToggleButton
                    class="ToggleButton"
                    label="ポケモンの道具"
                    v-model="singleProductSearchFormFields.kindPokemonTool"
                  />
                  <ToggleButton
                    class="ToggleButton"
                    label="基本エネ"
                    v-model="singleProductSearchFormFields.kindBasicEnergy"
                  />
                  <ToggleButton
                    class="ToggleButton"
                    label="特殊エネ"
                    v-model="singleProductSearchFormFields.kindSpecialEnergy"
                  />
                </div>
              </div>
            </FormBlock>
          </div>
          <div class="row">
            <FormBlock
              label="収録"
              align="horizontal"
              label-width="60px"
              content-width="calc(100% - 68px)"
            >
              <Select
                :options="packOptions"
                placeholder="選択してください"
                v-model="singleProductSearchFormFields.packId"
              />
            </FormBlock>
          </div>

          <div class="row double">
            <FormBlock
              label="レアリティ"
              align="horizontal"
              label-width="60px"
              content-width="calc(100% - 68px)"
            >
              <Select
                :options="rarityOptions"
                :placeholder="''"
                v-model="singleProductSearchFormFields.rarityId"
              />
            </FormBlock>
            <FormBlock
              label="レギュレーション"
              align="horizontal"
              label-width="60px"
              content-width="calc(100% - 68px)"
            >
              <Select
                :options="regulationOptions"
                :placeholder="''"
                v-model="singleProductSearchFormFields.regulationName"
              />
            </FormBlock>
          </div>

          <div class="row">
            <FormBlock
              label="販売価格"
              label-width="60px"
              align="horizontal"
              content-width="calc(100% - 68px)"
            >
              <div class="SellingPriceRange">
                <div class="SellingPrice">
                  <Input
                    placeholder="100"
                    type="number"
                    v-model="singleProductSearchFormFields.sellingPriceGte"
                    emit-null-number
                  />
                  <div class="unit">円</div>
                </div>
                <div class="separator">〜</div>
                <div class="SellingPrice">
                  <Input
                    placeholder="100000"
                    type="number"
                    v-model="singleProductSearchFormFields.sellingPriceLte"
                    emit-null-number
                  />
                  <div class="unit">円</div>
                </div>
              </div>
            </FormBlock>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script lang="ts">
import { defineNuxtComponent, useNuxtApp, useRouter } from "#app";
import {
  Input,
  IconButton,
  FormBlock,
  Select,
  ToggleButton,
  Accordion,
} from "@ka-nabellinc/ka-nabell-ui";
import type { SelectOption } from "@ka-nabellinc/ka-nabell-ui/src/components/Select";
import { NarrowDown } from "@ka-nabellinc/ka-nabell-icons";
import { storeToRefs } from "pinia";
import { DITokens } from "@/di";
import { ref, watch, computed } from "vue";
import type { PokemonPack, PokemonRarity } from "@/types/pokemon/pack";
import type { ResponseType as GetPacksResponseType } from "@/server/api/pokemon/packs/index.get";
import type { ResponseType as GetRaritiesResponseType } from "@/server/api/pokemon/rarities/index.get";

const regulationOptions: SelectOption[] = [
  { text: "", value: "" },
  { text: "スタンダード", value: "スタンダード" },
  { text: "エクストラ", value: "エクストラ" },
  { text: "殿堂", value: "殿堂" },
];

export default defineNuxtComponent({
  components: {
    Input,
    IconButton,
    Select,
    FormBlock,
    ToggleButton,
    Accordion,
    NarrowDown,
  },
  setup() {
    const { $container } = useNuxtApp();
    const pokemonStore = $container.resolve(DITokens.PokemonStore);
    const {
      showSingleProductSearchDetailMenu,
      hasSingleProductSearchFormDetailFields,
      singleProductSearchFormFields,
    } = storeToRefs(pokemonStore);
    const packs = ref<PokemonPack[]>([]);
    const packOptions = computed<SelectOption[]>(() =>
      packs.value.map(({ packName, packId, expansionMark }) => ({
        text: `${expansionMark ? `[${expansionMark}]` : ""}${packName}`,
        value: packId,
      }))
    );
    const rariries = ref<PokemonRarity[]>([]);
    const rarityOptions = computed<SelectOption[]>(() =>
      rariries.value.map(({ rarityId, rarityName }) => ({
        text: rarityName,
        value: rarityId,
      }))
    );
    const router = useRouter();
    const route = router.currentRoute;

    const search = () => {
      if (route.value.name !== "pokemon-search") {
        router.push({ name: "pokemon-search", query: { keyword: singleProductSearchFormFields.value.keyword } });
      }
    };

    // FIXME: storeで管理し、不要なfetchの回数を削減する
    const fetchPacks = async () => {
      try {
        const res = await $fetch<GetPacksResponseType>("/api/pokemon/packs");
        packs.value = res.packs;
      } catch (e) {
        console.log(e);
      }
    };

    const fetchRarities = async () => {
      try {
        const res = await $fetch<GetRaritiesResponseType>(
          "/api/pokemon/rarities"
        );
        rariries.value = res.rarities;
      } catch (e) {
        console.log(e);
      }
    };

    watch(
      router.currentRoute,
      async (val, oldVal) => {
        // detail modeの時のみページ読み込み後にclientでマスターデータをfetch
        if (val.meta.searchMode === "detail" && process.client) {
          // Packを一度だけ取得
          if (!packs.value.length) {
            await fetchPacks();
          }

          // Rarityを一度だけ取得
          if (!rariries.value.length) {
            await fetchRarities();
          }
        }

        // 検索ページから別ページに遷移する際に検索条件をリセット
        if (oldVal?.name === 'pokemon-search' && val.name !== 'pokemon-search') {
          pokemonStore.clearValue()
        }
      },
      { immediate: true }
    );

    return {
      search,
      singleProductSearchFormFields,
      hasSingleProductSearchFormDetailFields,
      showSingleProductSearchDetailMenu,
      route,

      packOptions,
      rarityOptions,
      regulationOptions,
    };
  },
});
</script>

<style scoped>
.PokemonSingleSerach {
  width: 100%;
  background: linear-gradient(
    89deg,
    #ffe0f3 0%,
    #ffe7da 20.83%,
    #fffacd 70.83%
  );
  position: relative;
}

.Head {
  height: 60px;
  width: 100%;
  padding: 12px 16px;
  display: flex;
  gap: 8px;
}

.Head > * {
  flex-shrink: 0;
}

.SearchInput {
  width: 100%;
}

.PokemonSingleSerach.detail .SearchInput {
  width: calc(100% - 8px - 36px);
}

.MenuButton {
  width: 36px;
  height: 36px;
}

.MenuIcon {
  width: 18px !important;
  height: 18px !important;
}

.DetailMenu {
  width: 100%;
  background: linear-gradient(
    89deg,
    #ffe0f3 0%,
    #ffe7da 20.83%,
    #fffacd 70.83%
  );
  z-index: 100;
}

.PokemonSingleSerach.detail .accordion-enter-active,
.PokemonSingleSerach.detail .accordion-leave-active {
  transition: all 0.4s ease;
}

.accordion-enter-active,
.accordion-leave-active {
  max-height: 282px;
}

.accordion-enter-from,
.accordion-leave-to {
  opacity: 0;
  max-height: 0px;
}

.DetailForm {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 4px 20px 12px;
}

.row {
  width: 100%;
}

.row > * {
  flex-shrink: 1;
}

.row.double {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
}

.row.double > * {
  width: 50%;
}

.kinds {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

.kindsRow {
  display: flex;
  gap: 8px;
  width: 100%;
  min-height: 38px;
}

.kindsRow > * {
  flex: 1;
}

.SellingPriceRange {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
}

.SellingPrice {
  display: flex;
  align-items: flex-end;
  gap: 4px;
  width: calc((100% - 14px - 4px - 4px) / 2);
}

.unit {
  font-size: 14px;
  line-height: 160%;
}

.separator {
  font-size: 14px;
  line-height: 160%;
}

.kindsRow.first .ToggleButton {
  max-width: calc((100% - 24px) / 4); /* 24px = gap * 3 */
}

.kindsRow.second .ToggleButton {
  max-width: calc((100% - 16px) / 3); /* 16px = gap * 2 */
}
</style>
